/**
 * WebAuthn / Passkey 操作 hook (#734).
 *
 * `@simplewebauthn/browser` の `startRegistration` / `startAuthentication` を
 * backend API と組み合わせて使う。返り値は React state とコールバック群。
 *
 * 設計詳細: docs/security/webauthn-passkey.md
 */
import { useCallback, useEffect, useState } from 'react'
import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { api, type WebAuthnConfig } from '../services/api'

export interface UseWebAuthnState {
  config: WebAuthnConfig | null
  loading: boolean
  supported: boolean
}

/**
 * WebAuthn config + browser support 判定 + register/authenticate ヘルパー.
 *
 * 戻り値の `enroll()`: 1 click で full registration flow を回す (= begin → startRegistration → complete).
 * 戻り値の `authenticate()`: 1 click で full assertion flow を回す (= begin → startAuthentication → complete)、
 *                           成功時は backend が発行した `authLogId` を返す (= 決済時の `webauthnAssertionId` として使う).
 */
export function useWebAuthn() {
  const [config, setConfig] = useState<WebAuthnConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const supported = typeof window !== 'undefined' && browserSupportsWebAuthn()

  useEffect(() => {
    let cancelled = false
    api.getWebauthnConfig()
      .then((c) => {
        if (!cancelled) setConfig(c)
      })
      .catch((e) => {
        console.warn('[useWebAuthn] config fetch failed:', e)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const enroll = useCallback(async (deviceLabel?: string): Promise<{ credentialId: string; credentialDeviceType?: string }> => {
    if (!supported) throw new Error('WebAuthn not supported on this browser')

    // 1. backend で challenge + options 生成
    const { options, challengeId } = await api.webauthnRegisterBegin()

    // 2. browser で credential 作成 (= 生体認証 / PIN)
    const credential = await startRegistration({
      optionsJSON: options as PublicKeyCredentialCreationOptionsJSON,
    })

    // 3. backend に attestation 送信 + 検証 + 保存
    // #1114 B: credentialDeviceType を上位に渡し、登録直後に型と意味を明示する。
    const result = await api.webauthnRegisterComplete(challengeId, credential, deviceLabel)
    return { credentialId: result.credentialId, credentialDeviceType: result.credentialDeviceType }
  }, [supported])

  const authenticate = useCallback(async (): Promise<{ authLogId: string; credentialId: string }> => {
    if (!supported) throw new Error('WebAuthn not supported on this browser')

    const { options, challengeId } = await api.webauthnAuthBegin()
    const credential = await startAuthentication({
      optionsJSON: options as PublicKeyCredentialRequestOptionsJSON,
    })
    const result = await api.webauthnAuthComplete(challengeId, credential)
    return result
  }, [supported])

  return {
    config,
    loading,
    supported,
    enroll,
    authenticate,
  } as const
}
